import { useEffect, useRef } from 'react';

// Makes the device/browser Back button close in-page overlays (modals, drawers,
// the mobile side menu, edit modes) instead of leaving the page. In the installed
// home-screen app, Back with no earlier history entry closes the whole app, so
// every overlay gets its own same-URL history entry while it is open.

const MARKER = '__backGuard';
const PENDING_BACK_TIMEOUT_MS = 1000;
let nextId = 1;
let listening = false;
let pendingBack = false;
let pendingTimer = null;
const stack = []; // { id, close, pushed }, oldest first

const currentMarker = () => window.history.state?.[MARKER] ?? 0;

// Keep React Router's own state (idx/key/usr) so the router sees no navigation.
const pushEntry = (guard) => {
  window.history.pushState({ ...window.history.state, [MARKER]: guard.id }, '');
  guard.pushed = true;
};

// Overlays opened while our own Back was in flight get their entry once it lands.
const pushWaitingEntries = () => stack.filter((guard) => !guard.pushed).forEach(pushEntry);

const isLeftoverEntry = (marker) => marker > 0 && !stack.some((guard) => guard.pushed && guard.id === marker);

const goBackSilently = () => {
  pendingBack = true;
  clearTimeout(pendingTimer);
  // Some browsers drop a queued Back when another entry is pushed; never wait forever.
  pendingTimer = setTimeout(() => {
    pendingBack = false;
    pushWaitingEntries();
  }, PENDING_BACK_TIMEOUT_MS);
  window.history.back();
};

const handlePopState = () => {
  pendingBack = false;
  clearTimeout(pendingTimer);
  const marker = currentMarker();

  // Close every overlay whose entry was popped, newest first.
  const popped = stack.filter((guard) => guard.pushed && guard.id > marker).reverse();
  popped.forEach((guard) => {
    stack.splice(stack.indexOf(guard), 1);
    guard.close();
  });

  // A leftover entry (overlay closed underneath another one, or by a navigation)
  // duplicates the URL below it, so skip it.
  if (isLeftoverEntry(marker)) {
    goBackSilently();
    return;
  }
  pushWaitingEntries();
};

export const registerBackGuard = (close) => {
  if (!listening) {
    window.addEventListener('popstate', handlePopState);
    listening = true;
  }
  const guard = { id: nextId++, close, pushed: false };
  stack.push(guard);
  if (!pendingBack) {
    pushEntry(guard);
  }
  return guard.id;
};

export const releaseBackGuard = (id) => {
  const index = stack.findIndex((guard) => guard.id === id);
  if (index === -1) return; // Already closed by Back.
  const [guard] = stack.splice(index, 1);
  // Closed from the UI: drop our entry so the next Back leaves the page as expected.
  if (guard.pushed && currentMarker() === id) {
    goBackSilently();
  }
};

const useBackButtonClose = (open, onClose) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const id = registerBackGuard(() => onCloseRef.current?.());
    return () => releaseBackGuard(id);
  }, [open]);
};

export default useBackButtonClose;
